import React from "react";
import { View, Alert } from "react-native";
import { Drawer } from "expo-router/drawer";
import { usePathname } from "expo-router";
import { DrawerActions, useNavigation } from "@react-navigation/native";
import { DrawerContent, FeedbackModal, HeaderButton } from "@/components";
import { colors, layout } from "@/constants";
import { CrisisResourcesProvider } from "@/hooks/crisis";
import {
  FeedbackProvider,
  useFeedback,
  useFeedbackSubmission
} from "@/hooks/feedback";
import { SessionProvider } from "@/hooks/session";
import { AuthGate } from "@/hooks/auth";

// Hamburger button that opens the drawer. Defined as its own component so
// useNavigation() runs in the drawer's screen context (headerLeft renders
// inside each Drawer.Screen, not at the Drawer's parent level).
function DrawerToggleButton() {
  const navigation = useNavigation();
  return (
    <View style={{ marginLeft: layout.space.sm }}>
      <HeaderButton
        icon="menu"
        onPress={() => navigation.dispatch(DrawerActions.toggleDrawer())}
        accessibilityLabel="Toggle menu"
      />
    </View>
  );
}

// Inner component that uses the session context
function DrawerLayoutInner() {
  const { isVisible, anchor, open, close } = useFeedback();
  const { submitFeedback, canSubmitFeedback } = useFeedbackSubmission();
  const pathname = usePathname();

  // Extract screen name from pathname (e.g., "/(drawer)/chat" -> "chat")
  const getScreenName = () => {
    const segments = pathname.split("/");
    return segments[segments.length - 1] || "unknown";
  };

  const handleFeedbackPress = () => {
    if (!canSubmitFeedback) {
      Alert.alert("Authentication Required", "Please log in to send feedback.");
      return;
    }
    open();
  };

  const handleFeedbackSubmit = async (feedback: {
    text: string;
    feedbackTypes: string[];
  }) => {
    // On rejection, the modal catches and renders the error message
    // (consistent with how chat.tsx surfaces session errors). The reported
    // turn (if any) is referenced by id only; the server resolves the message
    // and its surrounding context.
    await submitFeedback({
      text: feedback.text,
      feedbackTypes: feedback.feedbackTypes,
      screenName: getScreenName(),
      anchorMessageId: anchor?.id
    });
    Alert.alert("Thank You!", "Your feedback has been sent successfully.");
  };

  // Common header right component for all screens except login
  const feedbackButton = () => (
    <View style={{ marginRight: layout.space.sm }}>
      <HeaderButton
        icon="thumbs_up_down"
        onPress={handleFeedbackPress}
        accessibilityLabel="Send feedback"
      />
    </View>
  );

  return (
    <>
      <Drawer
        drawerContent={(props) => <DrawerContent {...props} />}
        screenOptions={{
          headerShown: true,
          headerTitle: "",
          headerStyle: {
            backgroundColor: colors.surface,
            elevation: 0,
            shadowOpacity: 0,
            borderBottomWidth: 0
          },
          headerTintColor: colors.primary,
          headerShadowVisible: false,
          headerLeft: () => <DrawerToggleButton />,
          drawerType: "front",
          drawerPosition: "left"
        }}
      >
        <Drawer.Screen
          name="login"
          options={{
            title: "Login"
          }}
        />
        <Drawer.Screen
          name="consent"
          options={{
            title: "Consent",
            drawerItemStyle: { display: "none" }, // Hide from drawer menu
            headerRight: feedbackButton
          }}
        />
        <Drawer.Screen
          name="settings"
          options={{
            title: "Settings",
            drawerItemStyle: { display: "none" }, // Hide from drawer menu during onboarding
            headerRight: feedbackButton
          }}
        />
        <Drawer.Screen
          name="facts"
          options={{
            title: "Facts",
            drawerItemStyle: { display: "none" }, // Hide from drawer menu, accessed via custom button
            headerRight: feedbackButton
          }}
        />
        <Drawer.Screen
          name="patterns"
          options={{
            title: "Patterns",
            drawerItemStyle: { display: "none" }, // Hide from drawer menu, accessed via custom button
            headerRight: feedbackButton
          }}
        />
        <Drawer.Screen
          name="account"
          options={{
            title: "Account",
            drawerItemStyle: { display: "none" },
            headerRight: feedbackButton
          }}
        />
        <Drawer.Screen
          name="admin"
          options={{
            title: "Admin",
            drawerItemStyle: { display: "none" },
            headerRight: feedbackButton
          }}
        />
        <Drawer.Screen
          name="chat"
          options={{
            title: "Chat",
            headerRight: feedbackButton
          }}
        />
      </Drawer>

      <FeedbackModal
        visible={isVisible}
        isAnchored={!!anchor}
        onClose={close}
        onSubmit={handleFeedbackSubmit}
      />
    </>
  );
}

export default function DrawerLayout() {
  return (
    <AuthGate>
      <SessionProvider>
        <CrisisResourcesProvider>
          <FeedbackProvider>
            <DrawerLayoutInner />
          </FeedbackProvider>
        </CrisisResourcesProvider>
      </SessionProvider>
    </AuthGate>
  );
}
